import { Injectable } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

/**
 * The fields of the library's per-config blob that have to outlive the trip to Keycloak.
 *
 * `authorize()` leaves the page, so the tab reloads before the callback runs, and every one of
 * these is written BEFORE that navigation and read AFTER it. None of them is a credential:
 * `authStateControl` and `authNonce` are single-use anti-CSRF / anti-replay values; `codeVerifier`
 * is worthless without the authorization code, which Keycloak hands only to the registered
 * redirect URI of this same tab; and `authWellKnownEndPoints` is the realm's discovery document,
 * which anyone can fetch unauthenticated from `${authority}/.well-known/openid-configuration` —
 * the callback path reads it out of storage and never re-queries it, so without it the token
 * request dies with `Token Endpoint not defined`. All of them are spent the moment the code is
 * redeemed, and this class deletes them then — see `mirrorHandshake`.
 */
const HANDSHAKE_FIELDS = [
  'authStateControl',
  'authNonce',
  'codeVerifier',
  'storageCodeFlowInProgress',
  'authWellKnownEndPoints',
] as const;

/** Namespace for the handshake mirror, so `clear()` can never wipe a key that is not ours. */
const HANDSHAKE_PREFIX = 'vault.oidc.handshake.';

/**
 * The library's own "a code flow is running right now" flag (`FlowsDataService.isCodeFlowInProgress`
 * / `setCodeFlowInProgress` / `resetCodeFlowInProgress` in angular-auth-oidc-client). It is the
 * first thing `StandardLoginService.loginStandard()` writes when `authorize()` is called — before
 * the discovery document, before `codeVerifier`, before anything else — and it goes back to
 * `false` the instant `CodeFlowCallbackService.authenticatedCallbackWithCode()` finishes handling
 * the callback, on both its success and its failure path. That makes it the right discriminator
 * for "should this write be mirrored": true exactly while a handshake is actually in flight,
 * false the rest of the time, regardless of anything else sitting in the blob.
 *
 * `authnResult` (the token response) does NOT track this: it is present in the blob for the
 * entire life of a session, and a live tab that already holds one can still start a SECOND
 * handshake — `authorize()` called again while nothing has logged the tab out. angular-auth-oidc-
 * client's own `StoragePersistenceService.write()` does a read-merge-write, so that second
 * handshake's fresh `authStateControl`/`authNonce`/`codeVerifier` land in the SAME object as the
 * old `authnResult`. A check keyed on "does the blob contain a token" sees that old token and
 * refuses to mirror the new handshake; the browser then leaves for Keycloak, the reload empties
 * the `Map`, and the callback comes back to nothing to match its `state` against — exactly the
 * `could not find matching config for state …` failure this class exists to prevent. Reproduced
 * end to end: log in once, call `authorize()` a second time in the same tab with the session still
 * valid (no expiry or revocation needed, since the stale token is ever-present), and the callback
 * throws that error. Keying on `storageCodeFlowInProgress` instead fixes it, because it is set
 * before the stale token ever gets a chance to matter.
 */
const CODE_FLOW_IN_PROGRESS_FIELD = 'storageCodeFlowInProgress';

/**
 * OIDC storage that keeps every token in memory only — never in localStorage, sessionStorage or a
 * cookie. This is the mitigation the public-client decision (D3) rests on: an access token lives
 * for five minutes inside one tab and disappears with it, so an XSS cannot lift a credential that
 * outlives the tab, and a page reload re-runs the authorization round trip against the Keycloak
 * session cookie instead of reading a persisted token.
 *
 * The one thing that cannot be held in memory is the PKCE handshake. `angular-auth-oidc-client`
 * hands this class a single key — the configId — whose value is the whole per-config state as
 * JSON, tokens and handshake fields together. `authorize()` then does a full page navigation, so a
 * pure `Map` is empty again by the time the callback returns and the library fails the callback
 * with `could not find matching config for state …`, leaving the app unable to log in at all.
 *
 * So the blob is split: everything stays in the `Map`, and a REDACTED copy carrying only
 * `HANDSHAKE_FIELDS` is mirrored in sessionStorage for exactly as long as `storageCodeFlowInProgress`
 * says a handshake is running — see `mirrorHandshake` and `CODE_FLOW_IN_PROGRESS_FIELD`. The token
 * response is never part of that copy, under any branch, and the copy is gone the instant the code
 * is redeemed (or the handshake fails) — which is why both web storages read back empty once a
 * session exists.
 *
 * This file is duplicated in the sibling repos, reach-frontend and suite-frontend, at the same
 * relative path (src/app/core/auth/in-memory-storage.ts) — same class, only `HANDSHAKE_PREFIX`
 * differs (`reach.oidc.handshake.` and `suite.oidc.handshake.` there, so one app's `clear()` can
 * never wipe another's handshake under the shared veflo.com domain). A fix made here does not
 * reach those copies, and vice versa: check all three when touching this logic.
 */
@Injectable({ providedIn: 'root' })
export class InMemoryStorage implements AbstractSecurityStorage {
  private readonly entries = new Map<string, string>();

  read(key: string): string | null {
    const inMemory = this.entries.get(key);
    if (inMemory !== undefined) {
      return inMemory;
    }

    // First read after the redirect: the Map is gone, the handshake is not.
    const mirrored = sessionStorage.getItem(HANDSHAKE_PREFIX + key);
    if (mirrored === null) {
      return null;
    }

    this.entries.set(key, mirrored);

    return mirrored;
  }

  write(key: string, value: string): void {
    this.entries.set(key, value);
    this.mirrorHandshake(key, value);
  }

  remove(key: string): void {
    this.entries.delete(key);
    sessionStorage.removeItem(HANDSHAKE_PREFIX + key);
  }

  clear(): void {
    this.entries.clear();
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(HANDSHAKE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  }

  /**
   * Keeps the sessionStorage copy in step with the blob, carrying the handshake fields and nothing
   * else. It drops the copy — rather than refreshing it — as soon as `storageCodeFlowInProgress`
   * says no handshake is actually running, so a completed login (or a login that never started)
   * leaves web storage as empty as it found it. Deciding this on whether a handshake is genuinely
   * in progress, rather than on whether a token response happens to be present, is what keeps a
   * live tab's SECOND `authorize()` — called while the first login's `authnResult` is still
   * sitting in the same blob — from having its mirror silently dropped. See
   * CODE_FLOW_IN_PROGRESS_FIELD for the full account of why that used to fail.
   */
  private mirrorHandshake(key: string, value: string): void {
    const mirrorKey = HANDSHAKE_PREFIX + key;
    const blob = parseBlob(value);

    if (blob === null || blob[CODE_FLOW_IN_PROGRESS_FIELD] !== true) {
      sessionStorage.removeItem(mirrorKey);
      return;
    }

    const handshake: Record<string, unknown> = {};
    for (const field of HANDSHAKE_FIELDS) {
      if (field in blob) {
        handshake[field] = blob[field];
      }
    }

    if (Object.keys(handshake).length === 0) {
      sessionStorage.removeItem(mirrorKey);
      return;
    }

    sessionStorage.setItem(mirrorKey, JSON.stringify(handshake));
  }
}

/** Returns the blob as an object, or null for anything this class must not try to redact. */
function parseBlob(value: string): Record<string, unknown> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    // Not the library's JSON blob. Fail closed: mirror nothing.
    return null;
  }

  return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
    ? (parsed as Record<string, unknown>)
    : null;
}
